/* @refresh reset */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RootState } from '../../core/store/store';
import { isPanelTeamMember } from '../panel/panelAccess/panelRoleAccess';
import { useKeyboardShortcuts } from '../../shared/hooks/useKeyboardShortcuts';
import useUserProfileSync from '../../shared/hooks/useUserProfileSync';
import usePageTranslation from '../../shared/hooks/usePageTranslation';
import BuildingsModal from './components/BuildingsModal';
import BuildingDetailModal from './components/BuildingDetailModal';
import TutorialCoach from './tutorial/TutorialCoach';
import useTutorial from './tutorial/useTutorial';
import InterventionManageModal from './components/interventions/InterventionManageModal';
import { useInterventionManageController } from './components/interventions/useInterventionManageController';
import { Batiment } from '../../shared/types/game';
import { openAppRouteInNewWindow } from '../../shared/utils/openAppRouteInNewWindow';
import { getApiBaseUrl } from '../../core/config/api';
import config from '../../config/index';
import {
  getInterventionDisplayLines,
  isTerminalInterventionStatus,
  normalizeInterventionInstanceStatut,
} from './components/interventions/instanceModel';
import { normalizeMainMapView, isDrawerView, mainMapViewLabel, type MainMapView } from './map/mapUtils';
import { type ContextMenuState, useMapData } from './map/hooks/useMapData';
import { useMaintenanceStatus } from './map/hooks/useMaintenanceStatus';
import { usePriseAppels } from './map/hooks/usePriseAppels';
import { useInterventions } from './map/hooks/useInterventions';
import { useMapSocket } from './map/hooks/useMapSocket';
import { useMapMetrics } from './map/hooks/useMapMetrics';
import { useInterventionFilters } from './map/hooks/useInterventionFilters';
import MapHeader from './map/MapHeader';
import MapRail, { RAIL_WIDTH } from './map/MapRail';
import ContextBar from './map/ContextBar';
import DispatchView from './map/views/DispatchView';
import MoyensSynopticView from './map/views/MoyensSynopticView';
import ReseauView from './map/views/ReseauView';
import ResourcesView from './map/views/ResourcesView';
import InterventionsView from './map/views/InterventionsView';
import BuildingsView from './map/views/BuildingsView';
import HistoriqueView from './map/views/HistoriqueView';
import TechTreeView from './map/views/TechTreeView';

const Map: React.FC = () => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  const { t, currentLang } = usePageTranslation('game', 'Map');
  useUserProfileSync(2000, Boolean(currentUser?.id));
  const [searchParams, setSearchParams] = useSearchParams();

  const [showBuildingsModal, setShowBuildingsModal] = useState(false);
  /** Fiche bâtiment ouverte depuis le cockpit (Étape 4). */
  const [detailBuildingId, setDetailBuildingId] = useState<number | null>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenuState>(null);
  const tutorial = useTutorial();
  const [showInterventionSectors, setShowInterventionSectors] = useState(
    () => typeof window !== 'undefined' && localStorage.getItem('mapShowInterventionSectors') === 'true',
  );
  const [selectedInterventionId, setSelectedInterventionId] = useState<number | null>(null);
  const [now, setNow] = useState(() => new Date());
  const [activeMainView, setActiveMainView] = useState<MainMapView>(
    () => normalizeMainMapView(searchParams.get('view')),
  );
  const [futureNotice, setFutureNotice] = useState<string | null>(null);
  const [interventionSearch, setInterventionSearch] = useState('');
  const [interventionPhaseFilter, setInterventionPhaseFilter] = useState<'all' | 'wait' | 'travel' | 'site'>('all');
  const [interventionSort, setInterventionSort] = useState<'priority' | 'newest' | 'oldest'>('priority');

  const futureNoticeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousBuildingsRef = useRef<Batiment[]>([]);
  const isInitialLoadRef = useRef(true);
  // Tutoriel : lignes de base pour détecter les actions réelles du joueur.
  const tutoBuildingCountRef = useRef<number | null>(null);
  const tutoClosedInterventionIdsRef = useRef<Set<number> | null>(null);
  const tutoInterventionRequestedRef = useRef(false);
  const titleLocale = currentLang === 'fr' ? 'fr-FR' : 'en-US';

  const { maintenanceStatus } = useMaintenanceStatus();
  const { priseAppels, setPriseAppels, priseLoadError, priseToggling, togglePriseAppels } =
    usePriseAppels(currentUser?.id);
  const { buildings, setBuildings, playerVehicles, playerPersonnel, loading, saveBuildings } =
    useMapData({ currentUser, setContextMenu });

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || '';
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, []);

  const { interventions, setInterventions, loadInterventions } = useInterventions({ currentUser, authHeaders });
  const { liveVehicleTracks } = useMapSocket({
    userId: currentUser?.id,
    setInterventions,
    setPriseAppels,
    loadInterventions,
  });

  const setMainView = useCallback(
    (nextView: MainMapView) => {
      setActiveMainView(nextView);
      const nextParams = new URLSearchParams(searchParams);
      if (nextView === 'dispatch') { nextParams.delete('view'); } else { nextParams.set('view', nextView); }
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const openDedicatedMainView = useCallback((view: MainMapView) => {
    openAppRouteInNewWindow(view === 'dispatch' ? '/game/map' : `/game/map?view=${view}`);
  }, []);

  const openInterventionsSynoptic = useCallback(() => setMainView('interventions'), [setMainView]);

  // ── Cockpit : les vues GESTION s'ouvrent en tiroir par-dessus la salle. ──
  // `opsView` = la vue OPS qui occupe la salle (dispatch/synoptique/historique) ;
  // `drawerView` = le tiroir GESTION ouvert par-dessus, ou null. La salle (dispatch)
  // n'est jamais démontée quand on ouvre un tiroir : elle continue de tourner derrière.
  const lastOpsViewRef = useRef<MainMapView>('dispatch');
  if (!isDrawerView(activeMainView)) lastOpsViewRef.current = activeMainView;
  const drawerView: MainMapView | null = isDrawerView(activeMainView) ? activeMainView : null;
  const opsView: MainMapView = isDrawerView(activeMainView) ? lastOpsViewRef.current : activeMainView;
  const closeDrawer = useCallback(() => setMainView(lastOpsViewRef.current), [setMainView]);

  /** Hauteur de la topbar (habillage proto) : rail et zone de travail démarrent dessous. */
  const HEADER_HEIGHT = 46;

  const handleFutureFeature = useCallback((featureLabel: string) => {
    setFutureNotice(`${featureLabel} : en cours de mise a jour, disponible prochainement.`);
    if (futureNoticeTimeoutRef.current) clearTimeout(futureNoticeTimeoutRef.current);
    futureNoticeTimeoutRef.current = setTimeout(() => {
      setFutureNotice(null);
      futureNoticeTimeoutRef.current = null;
    }, 3500);
  }, []);

  const handleBuildingsClick = useCallback(() => setShowBuildingsModal(true), []);

  /** Bâtiment dont la fiche est ouverte depuis le cockpit. */
  const detailBuilding = useMemo(
    () => (detailBuildingId == null ? null : buildings.find((b) => Number(b.id) === detailBuildingId) ?? null),
    [detailBuildingId, buildings],
  );

  const handleBuildingPlace = useCallback(
    async (lat: number, lng: number, buildingType: string, buildingName: string) => {
      if (!currentUser) return;
      setContextMenu(null);
      const displayName = buildingName?.trim() || buildingType;
      const existingCount = buildings.filter((b) => b.type === buildingType).length;
      const { baseCapacityPerLevel, capacityIncreasePerLevel } = config.buildings.capacity;
      const { initialLevel } = config.buildings.defaults;
      const initialCapacity = baseCapacityPerLevel + (initialLevel - 1) * capacityIncreasePerLevel;
      const newBuilding: Batiment = {
        id: Date.now(),
        nom: `${displayName} #${existingCount + 1}`,
        type: buildingType,
        niveau: initialLevel,
        user_id: currentUser.id,
        localisation: `${lat},${lng}`,
        capacite: initialCapacity,
        capacite_vehicules: initialCapacity,
        cout: 0,
        description: '',
        actif: true,
        postes_disponibles: initialCapacity,
        postes_occupes: 0,
        augmentations_capacite_personnel: 0,
        augmentations_capacite_vehicules: 0,
        augmentation_personnel_en_cours: null,
        augmentation_vehicules_en_cours: null,
        xp: 0,
        xp_requis_par_niveau: config.buildings.experience.xpRequiredPerLevel,
        created_at: new Date().toISOString(),
      };
      const updated = [...buildings, newBuilding];
      setBuildings(updated);
      await saveBuildings(updated);
    },
    [currentUser, buildings, setBuildings, saveBuildings],
  );

  const activeInterventions = useMemo(
    () =>
      [...interventions]
        .filter((inv) => !isTerminalInterventionStatus(inv.statut || ''))
        .sort((a, b) => {
          const ta = a.date_creation ? new Date(a.date_creation).getTime() : 0;
          const tb = b.date_creation ? new Date(b.date_creation).getTime() : 0;
          return tb - ta;
        }),
    [interventions],
  );

  const selectedIntervention =
    activeInterventions.find((inv) => inv.id === selectedInterventionId) ?? activeInterventions[0] ?? null;
  const selectedDisplay = selectedIntervention
    ? getInterventionDisplayLines(selectedIntervention, 'Intervention')
    : null;

  const {
    vehicleStatusMetrics,
    personnelMetrics,
    buildingOperationalRows,
    buildingTypeBreakdown,
    resourceAlerts,
    topMileageVehicles,
    buildingFilterOptions,
    vehicleTypeFilterOptions,
    selectedInterventionVehicleRows,
  } = useMapMetrics({
    buildings,
    playerVehicles,
    playerPersonnel,
    activeInterventions,
    selectedIntervention,
  });

  const { getInterventionPriority, setInterventionPriority, filteredInterventions, interventionPhaseCounts } =
    useInterventionFilters({
      activeInterventions,
      userId: currentUser?.id,
      interventionSearch,
      interventionPhaseFilter,
      interventionSort,
      setInterventions,
    });

  const manageController = useInterventionManageController({
    userId: currentUser?.id,
    instances: interventions,
    setInstances: setInterventions,
    loadInstances: loadInterventions,
    refreshInstances: loadInterventions,
    authHeaders,
    t,
    titleLocale,
    fetchVehiclesEnabled: true,
  });

  const hasPanelEntryAccess = useMemo(
    () => Boolean(currentUser?.roles_id && isPanelTeamMember(currentUser.roles_id)),
    [currentUser?.roles_id],
  );

  const availablePersonnel = playerPersonnel.filter((p) => p.actif).length;
  const availableVehicles = playerVehicles.length;

  /** Interventions encore sans engagement — repris dans la barre de contexte. */
  const waitingCount = useMemo(
    () =>
      activeInterventions.filter(
        (i) => normalizeInterventionInstanceStatut(i.statut || '') === 'EN_ATTENTE',
      ).length,
    [activeInterventions],
  );
  const headerClock = now.toLocaleString('fr-FR', {
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    day: '2-digit', month: 'short', year: 'numeric',
  });
  const availabilityPercent = Math.min(99, 60 + activeInterventions.length);
  const availabilityCircumference = 2 * Math.PI * 30;
  const availabilityOffset = availabilityCircumference * (1 - availabilityPercent / 100);

  const showScheduledBanner = Boolean(maintenanceStatus?.scheduled_at && !maintenanceStatus?.enabled);
  const showMaintenanceBanner = Boolean(maintenanceStatus?.enabled);

  useKeyboardShortcuts({
    onMapToggle: () => navigate('/dashboard'),
    enabled: !showBuildingsModal && !contextMenu,
  });

  // Échap ferme le tiroir GESTION et revient aux opérations.
  useEffect(() => {
    if (!drawerView) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMainView(lastOpsViewRef.current);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerView, setMainView]);

  useEffect(() => {
    const viewFromUrl = normalizeMainMapView(searchParams.get('view'));
    if (viewFromUrl !== activeMainView) setActiveMainView(viewFromUrl);
  }, [searchParams, activeMainView]);

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (activeInterventions.length === 0) { setSelectedInterventionId(null); return; }
    if (selectedInterventionId == null || !activeInterventions.some((inv) => inv.id === selectedInterventionId)) {
      setSelectedInterventionId(activeInterventions[0].id);
    }
  }, [activeInterventions, selectedInterventionId]);

  useEffect(() => {
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      previousBuildingsRef.current = buildings;
      return;
    }
    const hasChanged = JSON.stringify(buildings) !== JSON.stringify(previousBuildingsRef.current);
    if (hasChanged && currentUser) {
      void saveBuildings(buildings);
      previousBuildingsRef.current = buildings;
    }
  }, [buildings, currentUser, saveBuildings]);

  // ── Tutoriel : détection des actions réelles pour faire avancer les étapes ──
  const { reportEvent: reportTutorialEvent } = tutorial;

  // Nouveau bâtiment placé.
  useEffect(() => {
    const count = buildings.length;
    if (tutoBuildingCountRef.current === null) { tutoBuildingCountRef.current = count; return; }
    if (count > tutoBuildingCountRef.current) reportTutorialEvent('building');
    tutoBuildingCountRef.current = count;
  }, [buildings.length, reportTutorialEvent]);

  // Personnel recruté / véhicule acheté : signaux émis par les modales de gestion
  // (la carte ne rafraîchit pas ses propres listes après le montage).
  useEffect(() => {
    const onStaff = () => reportTutorialEvent('staff');
    const onVehicle = () => reportTutorialEvent('vehicle');
    window.addEventListener('opsecours:staff-recruited', onStaff);
    window.addEventListener('opsecours:vehicle-purchased', onVehicle);
    return () => {
      window.removeEventListener('opsecours:staff-recruited', onStaff);
      window.removeEventListener('opsecours:vehicle-purchased', onVehicle);
    };
  }, [reportTutorialEvent]);

  // Prise d'appels active (couvre aussi le cas où elle l'était déjà en arrivant sur l'étape).
  useEffect(() => {
    if (tutorial.stepData?.gate === 'prise-appels' && priseAppels) reportTutorialEvent('prise-appels');
  }, [tutorial.stepData?.gate, priseAppels, reportTutorialEvent]);

  // Étape finale : demande au serveur de générer l'intervention de tutoriel (is_tuto).
  // Endpoint idempotent : sans risque en cas de remontage.
  useEffect(() => {
    if (tutorial.stepData?.id !== 'firstIntervention' || tutoInterventionRequestedRef.current) return;
    tutoInterventionRequestedRef.current = true;
    void (async () => {
      try {
        const res = await fetch(`${getApiBaseUrl()}/api/cta/tutorial-intervention`, {
          method: 'POST',
          headers: authHeaders(),
        });
        if (res.ok) {
          await loadInterventions();
        } else {
          // Échec (ex: aucun scénario disponible) : on relâche le verrou pour
          // qu'un re-passage sur l'étape puisse retenter (le bouton « Continuer »
          // reste disponible pour ne jamais bloquer le joueur).
          tutoInterventionRequestedRef.current = false;
          console.warn('Spawn intervention tutoriel: réponse non OK', res.status);
        }
      } catch (e) {
        tutoInterventionRequestedRef.current = false;
        console.warn('Spawn intervention tutoriel:', e);
      }
    })();
  }, [tutorial.stepData?.id, authHeaders, loadInterventions]);

  // Intervention clôturée.
  useEffect(() => {
    const closedIds = new Set(
      interventions.filter((inv) => isTerminalInterventionStatus(inv.statut || '')).map((inv) => inv.id),
    );
    if (tutoClosedInterventionIdsRef.current === null) { tutoClosedInterventionIdsRef.current = closedIds; return; }
    const previous = tutoClosedInterventionIdsRef.current;
    let hasNewlyClosed = false;
    closedIds.forEach((id) => { if (!previous.has(id)) hasNewlyClosed = true; });
    if (hasNewlyClosed) reportTutorialEvent('intervention');
    tutoClosedInterventionIdsRef.current = closedIds;
  }, [interventions, reportTutorialEvent]);

  useEffect(() => {
    return () => { if (futureNoticeTimeoutRef.current) clearTimeout(futureNoticeTimeoutRef.current); };
  }, []);

  if (!currentUser) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 flex h-full w-full items-center justify-center bg-gray-900">
        <div className="text-lg text-white">Chargement de la carte...</div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 overflow-hidden text-slate-100"
      style={{ background: 'linear-gradient(180deg,#0b1220 0%,#060a11 100%)' }}
    >

      <MapHeader
        currentUser={currentUser}
        headerClock={headerClock}
        showInterventionSectors={showInterventionSectors}
        setShowInterventionSectors={setShowInterventionSectors}
        priseAppels={priseAppels}
        priseLoadError={priseLoadError}
        priseToggling={priseToggling}
        togglePriseAppels={togglePriseAppels}
        navigate={navigate}
        hasPanelEntryAccess={hasPanelEntryAccess}
      />

      <MapRail
        activeMainView={activeMainView}
        setMainView={setMainView}
        handleFutureFeature={handleFutureFeature}
        topOffset={HEADER_HEIGHT}
      />

      {futureNotice ? (
        <div className="absolute right-4 top-24 z-[1250] rounded-md border border-amber-500/45 bg-amber-950/90 px-3 py-2 text-xs text-amber-200 shadow-lg shadow-black/30">
          {futureNotice}
        </div>
      ) : null}

      <div
        className="absolute bottom-0 right-0 flex flex-col"
        style={{ top: `${HEADER_HEIGHT}px`, left: `${RAIL_WIDTH}px` }}
      >
        <ContextBar
          opsView={opsView}
          drawerView={drawerView}
          waitingCount={waitingCount}
          activeCount={Math.max(0, activeInterventions.length - waitingCount)}
          availableVehicles={availableVehicles}
          availablePersonnel={availablePersonnel}
          onCloseDrawer={closeDrawer}
        />
        <div className="min-h-0 flex-1 overflow-hidden p-3">
        {showScheduledBanner ? (
          <div className="mb-2 rounded-md border border-amber-500/50 bg-amber-900/85 px-3 py-2 text-xs text-amber-100 shadow-lg shadow-black/25">
            📅 {maintenanceStatus?.scheduled_message || 'Maintenance programmée'}{' '}
            {maintenanceStatus?.scheduled_at
              ? `— ${new Date(maintenanceStatus.scheduled_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`
              : ''}
          </div>
        ) : null}
        {showMaintenanceBanner ? (
          <div className="mb-2 rounded-md border border-orange-500/50 bg-orange-900/85 px-3 py-2 text-xs text-orange-100 shadow-lg shadow-black/25">
            🔧 {maintenanceStatus?.message || 'Maintenance en cours'}
          </div>
        ) : null}

        {opsView === 'dispatch' ? (
          <DispatchView
            activeInterventions={activeInterventions}
            selectedIntervention={selectedIntervention}
            selectedDisplay={selectedDisplay}
            setSelectedInterventionId={setSelectedInterventionId}
            getInterventionPriority={getInterventionPriority}
            setInterventionPriority={setInterventionPriority}
            setMainView={setMainView}
            buildings={buildings}
            interventions={interventions}
            liveVehicleTracks={liveVehicleTracks}
            showInterventionSectors={showInterventionSectors}
            contextMenu={contextMenu}
            setContextMenu={setContextMenu}
            handleBuildingPlace={handleBuildingPlace}
            availableVehicles={availableVehicles}
            availablePersonnel={availablePersonnel}
            availabilityPercent={availabilityPercent}
            availabilityCircumference={availabilityCircumference}
            availabilityOffset={availabilityOffset}
            handleFutureFeature={handleFutureFeature}
            playerVehicles={playerVehicles}
            selectedInterventionVehicleRows={selectedInterventionVehicleRows}
          />
        ) : null}

        {opsView === 'interventions' ? (
          <InterventionsView
            filteredInterventions={filteredInterventions}
            activeInterventions={activeInterventions}
            interventionPhaseCounts={interventionPhaseCounts}
            selectedIntervention={selectedIntervention}
            selectedDisplay={selectedDisplay}
            setSelectedInterventionId={setSelectedInterventionId}
            getInterventionPriority={getInterventionPriority}
            setInterventionPriority={setInterventionPriority}
            selectedInterventionVehicleRows={selectedInterventionVehicleRows}
            setMainView={setMainView}
            interventionSearch={interventionSearch}
            setInterventionSearch={setInterventionSearch}
            interventionPhaseFilter={interventionPhaseFilter}
            setInterventionPhaseFilter={setInterventionPhaseFilter}
            interventionSort={interventionSort}
            setInterventionSort={setInterventionSort}
            setManageId={manageController.setManageId}
            openDedicatedMainView={openDedicatedMainView}
          />
        ) : null}

        {opsView === 'moyens' ? (
          <MoyensSynopticView playerVehicles={playerVehicles} buildings={buildings} />
        ) : null}

        {opsView === 'historique' ? (
          <HistoriqueView />
        ) : null}
        </div>
      </div>

      {/* ── Tiroir GESTION : par-dessus la salle, qui continue de tourner derrière. ── */}
      {drawerView ? (
        <div
          className="absolute bottom-0 right-0 z-[1150]"
          style={{ top: `${HEADER_HEIGHT}px`, left: `${RAIL_WIDTH}px` }}
        >
          <button
            type="button"
            aria-label="Fermer et revenir aux opérations"
            onClick={closeDrawer}
            className="absolute inset-0 cursor-default bg-slate-950/55 backdrop-blur-[1px]"
          />
          <div className="relative mx-auto flex h-full w-full max-w-[1400px] flex-col p-3">
            <div className="flex items-center justify-between rounded-t-lg border border-b-0 border-slate-600/50 bg-slate-900/95 px-3 py-2">
              <div className="flex items-center gap-2">
                <span className="rounded bg-indigo-500/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-200">
                  Gestion
                </span>
                <span className="text-sm font-semibold text-white">{mainMapViewLabel(drawerView)}</span>
              </div>
              <button
                type="button"
                onClick={closeDrawer}
                className="rounded-md border border-slate-500/60 bg-slate-800/70 px-2 py-1 text-[11px] font-semibold text-slate-100 hover:bg-slate-700/80"
              >
                ← Retour aux opérations (Échap)
              </button>
            </div>
            <div className="flex-1 overflow-auto rounded-b-lg border border-slate-600/50 bg-slate-950/92 p-3">
              {drawerView === 'resources' ? (
                <ResourcesView
                  vehicleStatusMetrics={vehicleStatusMetrics}
                  personnelMetrics={personnelMetrics}
                  resourceAlerts={resourceAlerts}
                  topMileageVehicles={topMileageVehicles}
                  activeInterventions={activeInterventions}
                  playerVehicles={playerVehicles}
                  buildings={buildings}
                  buildingFilterOptions={buildingFilterOptions}
                  vehicleTypeFilterOptions={vehicleTypeFilterOptions}
                  setMainView={setMainView}
                  setSelectedInterventionId={setSelectedInterventionId}
                  openDedicatedMainView={openDedicatedMainView}
                  openInterventionsSynoptic={openInterventionsSynoptic}
                />
              ) : null}

              {drawerView === 'buildings' ? (
                <BuildingsView
                  buildingOperationalRows={buildingOperationalRows}
                  buildingTypeBreakdown={buildingTypeBreakdown}
                  handleBuildingsClick={handleBuildingsClick}
                  setMainView={setMainView}
                  openDedicatedMainView={openDedicatedMainView}
                  openInterventionsSynoptic={openInterventionsSynoptic}
                  onOpenBuilding={(id) => setDetailBuildingId(id)}
                />
              ) : null}

              {drawerView === 'reseau' ? (
                <ReseauView
                  playerVehicles={playerVehicles}
                  playerPersonnel={playerPersonnel}
                  buildings={buildings}
                />
              ) : null}

              {drawerView === 'tech-tree' ? <TechTreeView /> : null}
            </div>
          </div>
        </div>
      ) : null}

      <InterventionManageModal
        manageId={manageController.manageId}
        onClose={() => manageController.setManageId(null)}
        instances={interventions}
        availableVehicles={manageController.availableVehicles}
        selectedVehIdsByInstance={manageController.selectedVehIdsByInstance}
        toggleSectorVehicle={manageController.toggleSectorVehicle}
        selectAllSectorVehicles={manageController.selectAllSectorVehicles}
        clearSectorSelection={manageController.clearSectorSelection}
        sectorVehicles={manageController.sectorVehicles}
        sectorAlreadyEngaged={manageController.sectorAlreadyEngaged}
        sectorLoading={manageController.sectorLoading}
        sectorError={manageController.sectorError}
        busyId={manageController.busyId}
        assignVehicle={manageController.assignVehicle}
        advance={manageController.advance}
        unassignReservedVehicle={manageController.unassignReservedVehicle}
        recallInterventionVehicles={manageController.recallInterventionVehicles}
        recallInterventionVehicle={manageController.recallInterventionVehicle}
        reengageInterventionVehicle={manageController.reengageInterventionVehicle}
        assignSectorVehicle={manageController.assignSectorVehicle}
        handleDeploy={manageController.handleDeploy}
        manageCompletion={manageController.manageCompletion}
        completionRewards={manageController.completionRewards}
        clearCompletionRewards={manageController.clearCompletionRewards}
        titleLocale={manageController.titleLocale}
      />

      {/* Fiche bâtiment ouverte directement depuis le cockpit (Étape 4). */}
      {detailBuilding ? (
        <BuildingDetailModal
          building={detailBuilding}
          allBuildings={buildings}
          onClose={() => setDetailBuildingId(null)}
          onBuildingUpdate={(updatedBuilding) => {
            const updated = buildings.map((b) => (b.id === updatedBuilding.id ? updatedBuilding : b));
            setBuildings(updated);
            void saveBuildings(updated);
          }}
          onBuildingDelete={(deletedId) => {
            const updated = buildings.filter((b) => String(b.id) !== String(deletedId));
            setBuildings(updated);
            void saveBuildings(updated);
            setDetailBuildingId(null);
          }}
        />
      ) : null}

      <BuildingsModal
        isOpen={showBuildingsModal}
        onClose={() => setShowBuildingsModal(false)}
        buildings={buildings}
        onBuildingUpdate={(updatedBuilding) => {
          const updated = buildings.map((b) => (b.id === updatedBuilding.id ? updatedBuilding : b));
          setBuildings(updated);
          void saveBuildings(updated);
        }}
        onBuildingsChange={(updatedBuildings) => {
          setBuildings(updatedBuildings);
          void saveBuildings(updatedBuildings);
        }}
      />

      <TutorialCoach
        active={tutorial.active}
        step={tutorial.step}
        stepData={tutorial.stepData}
        totalSteps={tutorial.totalSteps}
        isFirstStep={tutorial.isFirstStep}
        isLastStep={tutorial.isLastStep}
        onNext={tutorial.next}
        onBack={tutorial.back}
        onSkip={tutorial.skip}
      />
    </div>
  );
};

export default Map;
